import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import NotificationBell from './NotificationBell';
import {
  Home,
  Globe,
  Briefcase,
  BookOpen,
  FileText,
  Award,
  DollarSign,
  Building,
  Plane,
  MapPin,
  Users,
  User,
  Bell,
  Menu,
  X,
  ChevronDown,
  ChevronRight,
  CheckCircle,
  Upload,
  TrendingUp,
  Mail,
  Shield,
  GraduationCap,
  Library,
  CreditCard,
  Lightbulb,
  LogOut
} from 'lucide-react';

// Organized navigation groups
const NAVIGATION_GROUPS = [
  {
    id: 'dashboard',
    name: 'Dashboard',
    icon: Home,
    path: '/dashboard',
    single: true, // Not a group, just a single link
  },
  {
    id: 'study-abroad',
    name: 'Study Abroad',
    icon: Globe,
    items: [
      { name: 'UK Pathfinder', path: '/dashboard/uk/pathfinder', icon: Globe },
      { name: 'Financial Analysis', path: '/dashboard/uk/financial-analysis', icon: DollarSign },
      { name: 'Readiness Check', path: '/dashboard/uk/readiness-assessment', icon: CheckCircle },
      { name: 'Application', path: '/dashboard/application', icon: Upload },
      { name: 'Progress', path: '/dashboard/progress', icon: TrendingUp },
      { name: 'Scholarships', path: '/services/scholarships', icon: Award },
      { name: 'Visa Assistance', path: '/services/visa', icon: FileText },
      { name: 'Accommodation', path: '/services/accommodation', icon: Building },
      { name: 'Pre-Departure', path: '/services/pre-departure', icon: Plane },
      { name: 'Post-Arrival', path: '/services/post-arrival', icon: MapPin },
    ],
  },
  {
    id: 'learning',
    name: 'Learning',
    icon: GraduationCap,
    items: [
      { name: 'Browse Courses', path: '/courses/catalog', icon: Library },
      { name: 'My Courses', path: '/my-courses', icon: BookOpen },
      { name: 'My Progress', path: '/learning/progress', icon: TrendingUp },
    ],
  },
  {
    id: 'exam-prep',
    name: 'Exam Prep',
    icon: FileText,
    items: [
      { name: 'Test Preparation', path: '/services/test-prep', icon: FileText },
      { name: 'Scratch Cards', path: '/scratch-cards', icon: CreditCard },
      { name: 'My Purchases', path: '/user/purchased-cards', icon: CheckCircle },
    ],
  },
  {
    id: 'career',
    name: 'Career & Skills',
    icon: Briefcase,
    items: [
      { name: 'Career Counseling', path: '/services/career', icon: Briefcase },
      { name: 'Skill Development', path: '/services/skills', icon: Lightbulb },
      { name: 'Alumni Network', path: '/services/alumni', icon: Users },
    ],
  },
];

// For educator role, add educator-specific items
const EDUCATOR_GROUPS = [
  {
    id: 'dashboard',
    name: 'Dashboard',
    icon: Home,
    path: '/dashboard/educator',
    single: true,
  },
  {
    id: 'courses',
    name: 'My Courses',
    icon: GraduationCap,
    items: [
      { name: 'All Courses', path: '/educator/courses', icon: Library },
      { name: 'Create Course', path: '/educator/courses/create', icon: Upload },
      { name: 'Content Library', path: '/educator/content', icon: BookOpen },
    ],
  },
  {
    id: 'students',
    name: 'Students',
    icon: Users,
    items: [
      { name: 'Student Management', path: '/educator/students', icon: Users },
    ],
  },
];

export default function DashboardLayout({ children, title, hideNavigation }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState([]); // Start with no groups expanded

  const toggleGroup = (groupId) => {
    setExpandedGroups(prev =>
      prev.includes(groupId)
        ? prev.filter(id => id !== groupId)
        : [...prev, groupId]
    );
  };

  // Determine which navigation to show based on user role
  const navigationGroups = user?.role === 'EDUCATOR' ? EDUCATOR_GROUPS : NAVIGATION_GROUPS;

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-50 w-64 bg-white border-r border-gray-200 transform transition-transform duration-300 lg:translate-x-0 ${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      }`}>
        <div className="h-full flex flex-col">
          {/* Logo - Fixed */}
          <div className="h-16 flex items-center justify-between px-6 border-b border-gray-200 flex-shrink-0">
            <Link to="/" className="flex items-center space-x-2">
              <div className="w-8 h-8 rounded-lg bg-yellow-500 flex items-center justify-center">
                <span className="text-white font-bold text-sm">E</span>
              </div>
              <span className="font-bold text-gray-900">Educential</span>
            </Link>
            <button
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden text-gray-500 hover:text-gray-700"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* User Role Badge - Fixed */}
          <div className="px-4 py-3 border-b border-gray-200 bg-gray-50 flex-shrink-0">
            <div className="flex items-center space-x-2 text-sm">
              {user?.role === 'EDUCATOR' ? (
                <>
                  <GraduationCap className="w-4 h-4 text-yellow-600" />
                  <span className="font-medium text-gray-700">Educator Portal</span>
                </>
              ) : (
                <>
                  <User className="w-4 h-4 text-gray-600" />
                  <span className="font-medium text-gray-700">Student Portal</span>
                </>
              )}
            </div>
          </div>

          {/* Navigation - Scrollable */}
          <nav className="flex-1 overflow-y-auto min-h-0">
            <div className="px-3 py-4 space-y-1">
              {navigationGroups.map((group) => {
                const GroupIcon = group.icon;
                const isExpanded = expandedGroups.includes(group.id);
                const isCurrentPath = window.location.pathname === group.path;

                // Single link (not a group)
                if (group.single) {
                  return (
                    <Link
                      key={group.id}
                      to={group.path}
                      className={`flex items-center space-x-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                        isCurrentPath
                          ? 'bg-yellow-50 text-yellow-700 border border-yellow-200'
                          : 'text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      <GroupIcon className="w-4 h-4 flex-shrink-0" />
                      <span>{group.name}</span>
                    </Link>
                  );
                }

                // Group with collapsible items
                return (
                  <div key={group.id} className="space-y-0.5">
                    {/* Group Header */}
                    <button
                      onClick={() => toggleGroup(group.id)}
                      className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-center space-x-3">
                        <GroupIcon className="w-4 h-4 flex-shrink-0" />
                        <span>{group.name}</span>
                      </div>
                      {isExpanded ? (
                        <ChevronDown className="w-4 h-4 text-gray-400" />
                      ) : (
                        <ChevronRight className="w-4 h-4 text-gray-400" />
                      )}
                    </button>

                    {/* Group Items */}
                    {isExpanded && (
                      <div className="ml-4 pl-3 border-l-2 border-gray-200 space-y-0.5">
                        {group.items.map((item) => {
                          const ItemIcon = item.icon;
                          const isActive = window.location.pathname === item.path;

                          return (
                            <Link
                              key={item.path}
                              to={item.path}
                              className={`flex items-center space-x-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                                isActive
                                  ? 'bg-yellow-50 text-yellow-700 font-medium'
                                  : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                              }`}
                            >
                              <ItemIcon className="w-4 h-4 flex-shrink-0" />
                              <span className="truncate">{item.name}</span>
                            </Link>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </nav>

          {/* Bottom Section - Fixed (Help & Profile) */}
          <div className="border-t border-gray-200 flex-shrink-0">
            {/* Help Section */}
            <div className="px-3 py-3">
              <a
                href="mailto:info.educential@gmail.com"
                className="flex items-center space-x-3 px-3 py-2 rounded-lg text-gray-700 hover:bg-gray-50 text-sm font-medium transition-colors"
              >
                <Mail className="w-4 h-4 flex-shrink-0" />
                <span>Get Help</span>
              </a>
            </div>

            {/* Profile Section */}
            <div className="px-3 pb-4 pt-2 border-t border-gray-200">
              <div className="relative group">
                <button className="w-full flex items-center space-x-3 px-3 py-2 rounded-lg hover:bg-gray-50 transition-colors">
                  <div className="w-9 h-9 rounded-full bg-yellow-100 flex items-center justify-center flex-shrink-0">
                    <User className="w-5 h-5 text-yellow-600" />
                  </div>
                  <div className="flex-1 text-left min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {user?.fullName || (user?.role === 'EDUCATOR' ? 'Educator' : 'Student')}
                    </p>
                    <p className="text-xs text-gray-500 truncate">{user?.email}</p>
                  </div>
                  <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />
                </button>

                {/* Dropdown Menu */}
                <div className="absolute bottom-full left-0 right-0 mb-2 bg-white border border-gray-200 rounded-lg shadow-lg py-1 hidden group-hover:block">
                  <Link
                    to="/profile"
                    className="flex items-center space-x-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    <User className="w-4 h-4 text-gray-500" />
                    <span>Profile</span>
                  </Link>
                  <Link
                    to="/settings"
                    className="flex items-center space-x-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    <Shield className="w-4 h-4 text-gray-500" />
                    <span>Settings</span>
                  </Link>
                  <div className="border-t border-gray-200 my-1"></div>
                  <button
                    onClick={logout}
                    className="w-full flex items-center space-x-3 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors"
                  >
                    <LogOut className="w-4 h-4" />
                    <span>Logout</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </aside>

      {/* Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-h-screen lg:ml-64">
        {/* Top Navbar */}
        <header className="h-16 bg-white border-b border-gray-200 sticky top-0 z-30">
          <div className="h-full flex items-center justify-between px-4 lg:px-6">
            {/* Left Side */}
            <div className="flex items-center space-x-4">
              <button
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden text-gray-500 hover:text-gray-700 p-2"
              >
                <Menu className="w-6 h-6" />
              </button>
              <h1 className="text-lg font-bold text-gray-900">{title || 'Dashboard'}</h1>
            </div>

            {/* Right Side */}
            <div className="flex items-center space-x-3">
              {/* Notifications */}
              <NotificationBell />
            </div>
          </div>
        </header>

        {/* Main Content Area */}
        <main className="flex-1 overflow-y-auto">
          {children}
        </main>
      </div>
    </div>
  );
}
